import { AfterContentInit, Component, Input, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { filter, finalize } from 'rxjs/operators';
import { cloneDeep } from 'lodash';

import { untilDestroyed } from '@app/core';
import { AppService, DocumentService, ErrorHandlerService, UserService, VerificationService } from '@app/services';
import {
  CustomSignerInput,
  Signer,
  SignerInput,
  SecurityVerificationEnum,
  CreditTypeEnum,
  SignerMainAttributes,
  SignerTypeEnum,
  User,
  DeliveryMethodEnum,
  Document,
  SignerInputContext,
  FallbackBehaviorEnum,
  UserBiometricValidation,
  UserBiometricValidationStatusEnum
} from '@app/models';

export type ModalResult = Signer;
export interface ModalPublicProperties {
  document: Document;
}

@Component({
  selector: 'app-create-signer-modal',
  templateUrl: './create-signer-modal.component.html',
  styleUrls: ['./create-signer-modal.component.scss', '../../documents-new/documents-new.component.scss'],
  standalone: false
})
export class CreateSignerModalComponent implements ModalPublicProperties, AfterContentInit, OnDestroy {
  @Input() document: Document;

  form: UntypedFormGroup;
  currentUser: User;
  isLoading = false;
  signer: CustomSignerInput;
  validationErrors: { [k: string]: any } = {};

  readonly SecurityVerificationEnum = SecurityVerificationEnum;
  readonly CreditTypeEnum = CreditTypeEnum;
  readonly SignerInputContext = SignerInputContext;

  constructor(
    public modal: NgbActiveModal,
    public appService: AppService,
    public documentService: DocumentService,
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private errorHandlerService: ErrorHandlerService,
    private verificationService: VerificationService
  ) {
    this.form = this.formBuilder.group({});
    this.signer = {
      email: '',
      action: this.documentService.roles[0],
      configs: {},
      _extra: { signerMainAttribute: SignerMainAttributes.Email, phoneNumber: '', documentType: SecurityVerificationEnum.Manual },
      security_verifications: []
    };
  }

  ngAfterContentInit() {
    this.userService
      .watchCurrentUser()
      .pipe(
        filter(user => !!user),
        untilDestroyed(this)
      )
      .subscribe({
        next: user => (this.currentUser = user)
      });

    setTimeout(() => (document.querySelector('app-create-signer-modal app-signer-input input') as HTMLInputElement).focus());
  }

  ngOnDestroy() {}

  create(signer: CustomSignerInput) {
    this.isLoading = true;
    this.validationErrors = {};
    this.documentService
      .createSigner({ documentId: this.document.id, signer: this.prepareSigner(signer) })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: data => this.modal.close(data as ModalResult),
        error: error => {
          const handledError = this.errorHandlerService.handleValidation(this.form, error, {
            ignoreGraphqlErrorNotifications: true,
            ignoreNotificationOn: ['user_biometric_verification_required']
          });
          this.validationErrors = handledError.validation || {};
          if (handledError.message === 'user_biometric_verification_required') {
            this.verificationService.validateUserCreateDocument().subscribe({
              next: (data: UserBiometricValidation) => {
                if (data?.status === UserBiometricValidationStatusEnum.Completed) {
                  this.create(signer);
                }
              }
            });
          }
        }
      });
  }

  onSignerChange() {
    this.validationErrors = {};
  }

  private prepareSigner(signer: CustomSignerInput): SignerInput {
    const newSigner = cloneDeep(signer);
    if (newSigner && newSigner._extra) {
      if (!newSigner._extra.showCpfVerification) {
        delete newSigner.configs?.cpf;
        delete newSigner.configs?.cnpj;
      }
      if (newSigner._extra.showSmsVerification && [SignerMainAttributes.Email, SignerMainAttributes.Name].includes(newSigner._extra.signerMainAttribute)) {
        newSigner.security_verifications.push({
          type: SecurityVerificationEnum.Sms,
          verify_phone: newSigner._extra.phoneNumber || null
        });
      }
      if (newSigner._extra.showSecurityVerifications && newSigner._extra.documentType) {
        newSigner.security_verifications.push({
          ...(newSigner._extra.fallbackEnabled ? {} : { fallback_behavior: FallbackBehaviorEnum.DisableFallback }),
          type: newSigner._extra.documentType as SecurityVerificationEnum
        });
      }
    }

    if ([SignerMainAttributes.Sms, SignerMainAttributes.Whatsapp].includes(newSigner._extra.signerMainAttribute)) {
      delete newSigner.name;
      delete newSigner.email;

      newSigner.phone = newSigner?.sms || newSigner?.whatsapp;

      delete newSigner.sms;
      delete newSigner.whatsapp;
    }

    if ([SignerMainAttributes.Email].includes(newSigner._extra.signerMainAttribute)) newSigner.delivery_method = DeliveryMethodEnum.DeliveryMethodEmail;
    if ([SignerMainAttributes.Name].includes(newSigner._extra.signerMainAttribute)) newSigner.delivery_method = DeliveryMethodEnum.DeliveryMethodLink;
    if ([SignerMainAttributes.Sms].includes(newSigner._extra.signerMainAttribute)) newSigner.delivery_method = DeliveryMethodEnum.DeliveryMethodSms;
    if ([SignerMainAttributes.Whatsapp].includes(newSigner._extra.signerMainAttribute)) newSigner.delivery_method = DeliveryMethodEnum.DeliveryMethodWhatsapp;

    if (newSigner._extra?.isQualified) newSigner.type = SignerTypeEnum.Qualified;
    delete newSigner._extra;
    if ((newSigner.security_verifications || []).length === 0) delete newSigner.security_verifications;
    if (Object.keys(newSigner?.configs || {}).length === 0) delete newSigner.configs;

    return newSigner;
  }
}
